import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useActiveConversation } from '../../chat/ActiveConversationContext';
import { initialChatMode, readChatModeFromUrl } from './chat-mode';

// Keeps the chat's size (`?chat`) and open thread (`?conversation`) in sync with
// the URL in both directions: it adopts a deep-link's size on first mount and
// opens on a handoff/greeting/pending-intent, and it is the single writer of both
// query params so a refresh (or a shared link) restores exactly what's on screen.
export function useChatUrlSync() {
  const router = useRouter();
  const { activeConversationId, chatMode, setChatMode, pendingChatAction, pendingUploadFiles, briefPeekId, conversationInUrl } = useActiveConversation();

  const initedRef = useRef(false);
  const prevHandoffRef = useRef(false);
  useEffect(() => {
    const handoff = router.query.new === '1' || typeof router.query.prefill === 'string';
    if (!initedRef.current) {
      initedRef.current = true;
      prevHandoffRef.current = handoff;
      const opening = initialChatMode({
        restored: readChatModeFromUrl(),
        handoff,
        hasConversation: typeof router.query.conversation === 'string',
      });
      if (opening) setChatMode(opening);
      return;
    }
    if (handoff && !prevHandoffRef.current) setChatMode('expanded');
    prevHandoffRef.current = handoff;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath]);

  // A stashed intent (start a call / attach files) is itself an open intent — it's
  // consumed by ChatSurface, which a bubble doesn't mount, and the URL transition
  // above misses the repeat case (the URL is often already ?new=1).
  useEffect(() => {
    if ((pendingChatAction || pendingUploadFiles) && chatMode === 'bubble') {
      setChatMode('expanded');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingChatAction, pendingUploadFiles]);

  const activeConversationRef = useRef(activeConversationId);
  activeConversationRef.current = activeConversationId;
  const chatModeRef = useRef(chatMode);
  chatModeRef.current = chatMode;
  // The morning-brief peek is transient — never stamp it into the URL, so a refresh
  // returns to the last real chat rather than resuming the brief.
  const briefPeekRef = useRef(briefPeekId);
  briefPeekRef.current = briefPeekId;
  // Only a deliberately-opened thread belongs in the URL. An ambiently-resumed
  // thread (from sessionStorage / the recent list) stays out of the address bar —
  // context + sessionStorage still resume it across refresh and routes.
  const conversationInUrlRef = useRef(conversationInUrl);
  conversationInUrlRef.current = conversationInUrl;
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Written via the History API (not the Next router) so it stays off
    // router.query — never fighting the autosend/handoff reads — and re-asserted
    // after each navigation, which drops query params.
    const sync = () => {
      const url = new URL(window.location.href);
      const p = url.searchParams;
      const conv = briefPeekRef.current || !conversationInUrlRef.current ? null : activeConversationRef.current;
      const mode = chatModeRef.current;
      if (conv) p.set('conversation', conv);
      else p.delete('conversation');
      if (mode === 'bubble' && !conv) p.delete('chat');
      else p.set('chat', mode);
      if (url.href !== window.location.href) {
        window.history.replaceState(window.history.state, '', url.href);
      }
    };
    sync();
    router.events.on('routeChangeComplete', sync);
    return () => router.events.off('routeChangeComplete', sync);
  }, [activeConversationId, chatMode, briefPeekId, conversationInUrl, router.events, router.pathname]);
}
